import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { api, ApiError } from '../../api/cliente.js';
import { Botao } from '../../componentes/ui/Botao.js';
import { Modal } from '../../componentes/ui/Modal.js';

interface LinhaPrevia {
  ip: string;
  ptr: string;
  problemas: string[];
}

interface ModalMassaProps {
  aberto: boolean;
  aoFechar: () => void;
  blocoId: string;
  versao: 4 | 6;
  /** Endereços selecionados; vazio significa "todos os visíveis". */
  ips: string[];
  /** Máscaras sugeridas (templates da operadora do bloco); a 1ª é o padrão. */
  sugestoes: { rotulo: string; mascara: string }[];
  aoAplicar: () => void;
}

/** Fallback quando a operadora do bloco não tem template nem domínio próprio. */
const MASCARA_PADRAO = 'host-{octeto}.rev.consultnetworks.com.br';

const TOKENS = [
  { token: '{octeto}', ajuda: 'último octeto do IPv4' },
  { token: '{octeto3}', ajuda: 'terceiro octeto — útil em blocos maiores que /24' },
  { token: '{ip-hifen}', ajuda: 'endereço com hífen no lugar dos separadores' },
  { token: '{ip}', ajuda: 'endereço como está' },
];

export function ModalMassa({
  aberto,
  aoFechar,
  blocoId,
  versao,
  ips,
  sugestoes,
  aoAplicar,
}: ModalMassaProps) {
  const [mascara, setMascara] = useState(sugestoes[0]?.mascara ?? MASCARA_PADRAO);
  const [previa, setPrevia] = useState<LinhaPrevia[]>([]);
  const [erroPrevia, setErroPrevia] = useState<string | null>(null);

  // Ao abrir, parte do domínio do cliente: a primeira sugestão vem dos templates
  // da operadora do bloco. Cai no padrão da Consult só se não houver nenhuma.
  useEffect(() => {
    if (aberto) setMascara(sugestoes[0]?.mascara ?? MASCARA_PADRAO);
    // Reagir só à abertura — não a cada tecla do usuário na máscara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  // A prévia é recalculada no servidor, e não no navegador, para usar
  // exatamente a mesma expansão de máscara que vai gravar. Duas implementações
  // — uma para mostrar e outra para aplicar — divergiriam em algum caso de
  // borda, e o usuário confirmaria uma coisa e gravaria outra.
  useEffect(() => {
    if (!aberto || ips.length === 0) return;

    const timer = setTimeout(() => {
      api
        .post<LinhaPrevia[]>(`/blocos/${blocoId}/ptr/previa`, {
          mascara,
          ips: ips.slice(0, 8),
          versao,
        })
        .then((r) => {
          setPrevia(r);
          setErroPrevia(null);
        })
        .catch((err: unknown) => {
          setPrevia([]);
          setErroPrevia(err instanceof ApiError ? err.mensagem : 'Máscara inválida');
        });
    }, 300); // espera o usuário parar de digitar

    return () => clearTimeout(timer);
  }, [aberto, mascara, ips, blocoId, versao]);

  const aplicar = useMutation({
    mutationFn: () => api.post<{ aplicados: number }>(`/blocos/${blocoId}/ptr/massa`, { mascara, ips }),
    onSuccess: () => {
      aoAplicar();
      aoFechar();
    },
  });

  const comProblema = previa.filter((p) => p.problemas.length > 0);

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      largura="largo"
      titulo="Editar reversos em massa"
      descricao={`A máscara será aplicada a ${ips.length} ${ips.length === 1 ? 'endereço' : 'endereços'}.`}
      rodape={
        <>
          <Botao onClick={aoFechar} disabled={aplicar.isPending}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            onClick={() => aplicar.mutate()}
            carregando={aplicar.isPending}
            disabled={!!erroPrevia || comProblema.length > 0 || ips.length === 0}
          >
            Aplicar e publicar
          </Botao>
        </>
      }
    >
      <label className="block">
        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
          Máscara
        </span>
        <input
          value={mascara}
          onChange={(e) => setMascara(e.target.value)}
          className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
        />
      </label>

      {sugestoes.length > 0 && (
        <div className="mt-3">
          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
            Modelos da operadora
          </span>
          <div className="flex flex-wrap gap-1.5">
            {sugestoes.map((s) => (
              <button
                key={s.mascara}
                type="button"
                title={s.mascara}
                onClick={() => setMascara(s.mascara)}
                className={`rounded-chip border px-2.5 py-1 text-[11.5px] font-semibold ${
                  mascara === s.mascara
                    ? 'border-marca bg-tint text-marca'
                    : 'border-borda bg-fundo text-texto-medio hover:border-marca hover:text-marca'
                }`}
              >
                {s.rotulo}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-2.5 flex flex-wrap gap-1.5">
        <span className="w-full text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
          Tokens
        </span>
        {TOKENS.map(({ token, ajuda }) => (
          <button
            key={token}
            type="button"
            title={ajuda}
            onClick={() => setMascara((m) => m + token)}
            className="rounded-chip border border-borda bg-fundo px-2 py-1 font-mono text-[11.5px] text-texto-medio hover:border-marca hover:text-marca"
          >
            {token}
          </button>
        ))}
      </div>

      {erroPrevia && (
        <p className="mt-4 flex items-start gap-2 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
          <AlertTriangle size={15} className="mt-px shrink-0" />
          {erroPrevia}
        </p>
      )}

      {previa.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
            Prévia {ips.length > previa.length && `(${previa.length} de ${ips.length})`}
          </p>
          <div className="overflow-hidden rounded-chip border border-borda">
            <table className="w-full border-collapse text-[12.5px]">
              <tbody>
                {previa.map((p) => (
                  <tr key={p.ip} className="border-b border-borda-suave last:border-0">
                    <td className="w-[38%] px-3 py-2 font-mono text-texto-suave">{p.ip}</td>
                    <td className="px-3 py-2 font-mono">
                      {p.ptr}
                      {p.problemas.length > 0 && (
                        <span className="ml-2 text-[11.5px] font-semibold text-erro">
                          {p.problemas[0]}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {aplicar.error && (
        <p className="mt-4 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
          {(aplicar.error as ApiError).mensagem}
        </p>
      )}
    </Modal>
  );
}
